import React, { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import Tab from 'react-bootstrap/Tab';
import { useTranslation } from 'react-i18next';

import { US_STATES } from '../../resources/constants';
import {
  EMPTY_CUSTOMER_DETAIL,
  newEmptyAddress,
  newEmptyContact,
  validateCustomerDetail,
} from './customerFormUtils';
import type { CustomerDetail, CustomerLookups } from './types';

const TRANSACTION_OPTIONS = [
  { value: 'INVOICES', labelKey: 'form.prefInvoices' },
  { value: 'PAYMENT_RECEIPTS', labelKey: 'form.prefPaymentReceipts' },
  { value: 'CREDIT_MEMOS', labelKey: 'form.prefCreditMemos' },
];

const TABS = ['general', 'addresses', 'contacts', 'financial', 'preferences'] as const;
type TabKey = (typeof TABS)[number];
type Address = CustomerDetail['addresses'][number];
type Contact = CustomerDetail['contacts'][number];

interface Props {
  show: boolean;
  saving: boolean;
  initial: CustomerDetail;
  lookups: CustomerLookups | null;
  serverErrors?: Record<string, string>;
  onClose: () => void;
  onSubmit: (values: CustomerDetail) => void;
}

function cloneDetail(detail: CustomerDetail): CustomerDetail {
  return JSON.parse(JSON.stringify(detail)) as CustomerDetail;
}

const CustomerFormModal: React.FC<Props> = ({ show, saving, initial, lookups, serverErrors = {}, onClose, onSubmit }) => {
  const { t } = useTranslation('scenes.customerCenter');
  const [values, setValues] = useState<CustomerDetail>(cloneDetail(initial));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<TabKey>('general');
  const isEdit = Boolean(initial.id);

  useEffect(() => {
    if (!show) return;
    setValues(cloneDetail(initial));
    setErrors({});
    setActiveTab('general');
  }, [show, initial]);

  useEffect(() => {
    if (!show || !Object.keys(serverErrors).length) return;
    setErrors((prev) => ({ ...prev, ...serverErrors }));
    setActiveTab('general');
  }, [show, serverErrors]);

  const errText = (key: string) => {
    const code = errors[key];
    if (!code) return null;
    if (code.startsWith('validation.') || code.startsWith('form.')) return t(code);
    return code;
  };

  const setField = <K extends keyof CustomerDetail>(key: K, value: CustomerDetail[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const setFinancial = (patch: Partial<CustomerDetail['financial']>) => {
    setValues((prev) => ({ ...prev, financial: { ...prev.financial, ...patch } }));
  };

  const patchAddress = (index: number, patch: Partial<Address>) => {
    setValues((prev) => ({ ...prev, addresses: prev.addresses.map((a, i) => (i === index ? { ...a, ...patch } : a)) }));
  };

  const patchContact = (index: number, patch: Partial<Contact>) => {
    setValues((prev) => ({ ...prev, contacts: prev.contacts.map((c, i) => (i === index ? { ...c, ...patch } : c)) }));
  };

  const handleSubmit = () => {
    const nextErrors = validateCustomerDetail(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const first = Object.keys(nextErrors)[0];
      if (first.startsWith('contacts')) setActiveTab('contacts');
      else if (first.startsWith('financial')) setActiveTab('financial');
      else if (first.startsWith('preferred')) setActiveTab('preferences');
      else setActiveTab('general');
      return;
    }
    onSubmit(values);
  };

  const stateOptions = useMemo(() => {
    if (lookups?.states?.length) return lookups.states.map((s) => ({ value: s.code, label: `${s.code} — ${s.name}` }));
    return US_STATES.map((s) => ({ value: s.code, label: `${s.code} — ${s.name}` }));
  }, [lookups]);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable dialogClassName="customer-center-form" data-testid="customer-form-modal">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? t('form.editTitle') : t('form.addTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="customer-center__form-body">
        {errors._form ? <Alert variant="danger">{errors._form}</Alert> : null}
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab((k as TabKey) || 'general')}>
          <Nav variant="tabs" className="mb-3">
            {TABS.map((tab) => (
              <Nav.Item key={tab}>
                <Nav.Link eventKey={tab} data-testid={`customer-form-tab-${tab}`}>{t(`form.tabs.${tab}`)}</Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="general">
              <div className="customer-center__form-grid">
                <Form.Group>
                  <Form.Label>{t('form.name')} *</Form.Label>
                  <Form.Control value={values.name} onChange={(e) => setField('name', e.target.value)} isInvalid={Boolean(errors.name)} data-testid="customer-name" />
                  <Form.Control.Feedback type="invalid">{errText('name')}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.displayName')}</Form.Label>
                  <Form.Control value={values.display_name || ''} onChange={(e) => setField('display_name', e.target.value || null)} isInvalid={Boolean(errors.display_name)} />
                  <Form.Control.Feedback type="invalid">{errText('display_name')}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.email')}</Form.Label>
                  <Form.Control type="email" value={values.email || ''} onChange={(e) => setField('email', e.target.value || null)} isInvalid={Boolean(errors.email)} />
                  <Form.Control.Feedback type="invalid">{errText('email')}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.phone')}</Form.Label>
                  <Form.Control value={values.phone || ''} onChange={(e) => setField('phone', e.target.value || null)} isInvalid={Boolean(errors.phone)} />
                  <Form.Control.Feedback type="invalid">{errText('phone')}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.fax')}</Form.Label>
                  <Form.Control value={values.fax} onChange={(e) => setField('fax', e.target.value)} isInvalid={Boolean(errors.fax)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.website')}</Form.Label>
                  <Form.Control value={values.website} onChange={(e) => setField('website', e.target.value)} isInvalid={Boolean(errors.website)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.category')}</Form.Label>
                  <Form.Select
                    value={values.category_id ?? ''}
                    onChange={(e) => setField('category_id', e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">{t('form.none')}</option>
                    {(lookups?.categories || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.ein')}</Form.Label>
                  <Form.Control value={values.ein} onChange={(e) => setField('ein', e.target.value)} />
                </Form.Group>
              </div>
              <div className="customer-center__form-switches">
                <Form.Check type="switch" id="customer-active" label={t('form.active')} checked={values.active} onChange={(e) => setField('active', e.target.checked)} />
                <Form.Check type="switch" id="customer-1099" label={t('form.is1099')} checked={values.is_1099_enabled} onChange={(e) => setField('is_1099_enabled', e.target.checked)} />
              </div>
            </Tab.Pane>
            <Tab.Pane eventKey="addresses">
              {values.addresses.length === 0 ? <p className="customer-center__form-empty">{t('form.noAddresses')}</p> : null}
              {values.addresses.map((addr, index) => (
                <div key={index} className="customer-center__form-card">
                  <div className="customer-center__form-grid">
                    <Form.Control className="customer-center__span-2" aria-label={t('form.address1')} placeholder={t('form.address1')} value={addr.address_line1} onChange={(e) => patchAddress(index, { address_line1: e.target.value })} />
                    <Form.Control className="customer-center__span-2" aria-label={t('form.address2')} placeholder={t('form.address2')} value={addr.address_line2} onChange={(e) => patchAddress(index, { address_line2: e.target.value })} />
                    <Form.Control aria-label={t('form.city')} placeholder={t('form.city')} value={addr.city} onChange={(e) => patchAddress(index, { city: e.target.value })} />
                    <div className="customer-center__form-grid customer-center__form-grid--tight">
                      <Form.Select aria-label={t('form.state')} value={addr.state} onChange={(e) => patchAddress(index, { state: e.target.value })}>
                        <option value="">{t('form.state')}</option>
                        {stateOptions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </Form.Select>
                      <Form.Control aria-label={t('form.zip')} placeholder={t('form.zip')} value={addr.zip_code} onChange={(e) => patchAddress(index, { zip_code: e.target.value })} />
                    </div>
                  </div>
                  <div className="customer-center__form-card-foot">
                    <Form.Check type="checkbox" id={`addr-billing-${index}`} label={t('form.defaultBilling')} checked={addr.default_billing} onChange={(e) => patchAddress(index, { default_billing: e.target.checked })} />
                    <Form.Check type="checkbox" id={`addr-shipping-${index}`} label={t('form.defaultShipping')} checked={addr.default_shipping} onChange={(e) => patchAddress(index, { default_shipping: e.target.checked })} />
                    <Button variant="outline-danger" size="sm" className="ms-auto" onClick={() => setField('addresses', values.addresses.filter((_, i) => i !== index))}>{t('form.remove')}</Button>
                  </div>
                </div>
              ))}
              <Button variant="outline-primary" size="sm" onClick={() => setField('addresses', [...values.addresses, newEmptyAddress()])}>{t('form.addAddress')}</Button>
            </Tab.Pane>
            <Tab.Pane eventKey="contacts">
              {values.contacts.length === 0 ? <p className="customer-center__form-empty">{t('form.noContacts')}</p> : null}
              {values.contacts.map((contact, index) => (
                <div key={index} className="customer-center__form-card">
                  <div className="customer-center__form-grid">
                    <Form.Control aria-label={t('form.firstName')} placeholder={t('form.firstName')} value={contact.first_name} isInvalid={Boolean(errors[`contacts.${index}.first_name`])} onChange={(e) => patchContact(index, { first_name: e.target.value })} />
                    <Form.Control aria-label={t('form.lastName')} placeholder={t('form.lastName')} value={contact.last_name} isInvalid={Boolean(errors[`contacts.${index}.last_name`])} onChange={(e) => patchContact(index, { last_name: e.target.value })} />
                    <Form.Control aria-label={t('form.contactEmail')} placeholder={t('form.contactEmail')} value={contact.email} isInvalid={Boolean(errors[`contacts.${index}.email`])} onChange={(e) => patchContact(index, { email: e.target.value })} />
                    <Form.Control aria-label={t('form.contactPhone')} placeholder={t('form.contactPhone')} value={contact.phone} onChange={(e) => patchContact(index, { phone: e.target.value })} />
                  </div>
                  <div className="customer-center__form-card-foot">
                    <Button variant="outline-danger" size="sm" className="ms-auto" onClick={() => setField('contacts', values.contacts.filter((_, i) => i !== index))}>{t('form.remove')}</Button>
                  </div>
                </div>
              ))}
              <Button variant="outline-primary" size="sm" onClick={() => setField('contacts', [...values.contacts, newEmptyContact()])}>{t('form.addContact')}</Button>
            </Tab.Pane>
            <Tab.Pane eventKey="financial">
              <div className="customer-center__form-grid">
                <Form.Group>
                  <Form.Label>{t('form.currency')}</Form.Label>
                  <Form.Control value={values.financial.currency} onChange={(e) => setFinancial({ currency: e.target.value })} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.paymentTerm')}</Form.Label>
                  <Form.Select value={values.financial.payment_term_id ?? ''} onChange={(e) => setFinancial({ payment_term_id: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">{t('form.none')}</option>
                    {(lookups?.payment_terms || []).map((pt) => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.receivableAccount')}</Form.Label>
                  <Form.Control value={values.financial.receivable_account} onChange={(e) => setFinancial({ receivable_account: e.target.value })} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('form.taxId')}</Form.Label>
                  <Form.Control value={values.financial.tax_id} isInvalid={Boolean(errors['financial.tax_id'])} onChange={(e) => setFinancial({ tax_id: e.target.value })} />
                </Form.Group>
                {values.financial.hold_status ? (
                  <Form.Group>
                    <Form.Label>{t('form.creditLimit')}</Form.Label>
                    <Form.Control value={values.financial.credit_limit || ''} isInvalid={Boolean(errors['financial.credit_limit'])} onChange={(e) => setFinancial({ credit_limit: e.target.value || null })} />
                  </Form.Group>
                ) : null}
                {values.financial.taxable ? (
                  <Form.Group>
                    <Form.Label>{t('form.taxCode')}</Form.Label>
                    <Form.Select value={values.financial.tax_code_id ?? ''} isInvalid={Boolean(errors['financial.tax_code_id'])} onChange={(e) => setFinancial({ tax_code_id: e.target.value ? Number(e.target.value) : null })}>
                      <option value="">{t('form.none')}</option>
                      {(lookups?.tax_codes || []).map((tc) => <option key={tc.id} value={tc.id}>{tc.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                ) : null}
              </div>
              <div className="customer-center__form-switches">
                <Form.Check type="switch" id="customer-hold" label={t('form.holdStatus')} checked={values.financial.hold_status} onChange={(e) => setFinancial({ hold_status: e.target.checked })} />
                <Form.Check type="switch" id="customer-taxable" label={t('form.taxable')} checked={values.financial.taxable} onChange={(e) => setFinancial({ taxable: e.target.checked })} />
              </div>
            </Tab.Pane>
            <Tab.Pane eventKey="preferences">
              <Form.Check
                type="checkbox"
                id="customer-pref-communication"
                className="mb-3"
                label={t('form.prefCommunication')}
                checked={values.preferences.preferred_communication}
                isInvalid={Boolean(errors.preferred_communication)}
                onChange={(e) => setField('preferences', { ...values.preferences, preferred_communication: e.target.checked })}
              />
              <div className="fw-semibold mb-2">{t('form.prefTransactionsLabel')}</div>
              {TRANSACTION_OPTIONS.map((opt) => (
                <Form.Check
                  key={opt.value}
                  type="checkbox"
                  id={`customer-pref-${opt.value}`}
                  label={t(opt.labelKey)}
                  checked={values.preferences.preferred_transactions.includes(opt.value)}
                  onChange={(e) => {
                    const set = new Set(values.preferences.preferred_transactions);
                    if (e.target.checked) set.add(opt.value); else set.delete(opt.value);
                    setField('preferences', { ...values.preferences, preferred_transactions: Array.from(set) });
                  }}
                />
              ))}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving} data-testid="customer-form-cancel">{t('form.cancel')}</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving} data-testid="customer-form-save">{saving ? t('form.saving') : t('form.save')}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomerFormModal;
export { EMPTY_CUSTOMER_DETAIL };
